import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@my-toolbox-fe/environments/environment';
import { Filter } from '@my-toolbox-fe/type-filter';
import {
  BrandModelsMap,
  BrandModelData,
  SubrangeCompositionResponse,
} from '@my-toolbox-fe/type-upload';
import { catchError, firstValueFrom, map, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SubrangeCompositionService {
  private http = inject(HttpClient);

  async getModelsByBrandForSubrange(subranges: Filter[]): Promise<BrandModelsMap> {
    if (!subranges || subranges.length === 0) {
      return Promise.resolve({});
    }

    const codesParam = this.extractSubrangeCodes(subranges);

    if (!codesParam || codesParam.length === 0) {
      return Promise.resolve({});
    }

    return await firstValueFrom(
      this.http
        .get<SubrangeCompositionResponse>(
          `${environment.baseUrl}/composition/subranges?codes=${codesParam}`,
        )
        .pipe(
          map((response) => this.transformResponseToBrandModelsMap(response)),
          catchError((error: HttpErrorResponse) => {
            return throwError(() => error);
          }),
        ),
    );
  }

  private extractSubrangeCodes(subranges: Filter[] | string[]): string {
    return subranges
      .map((sr) => (typeof sr === 'string' ? sr : (sr as any)?.value))
      .filter((code) => code && String(code).trim())
      .join(',');
  }

  private transformResponseToBrandModelsMap(response: SubrangeCompositionResponse): BrandModelsMap {
    const brandModelsMap: BrandModelsMap = {};

    if (!Array.isArray(response)) {
      return brandModelsMap;
    }

    response.forEach((brandData) => {
      const brand = brandData.brand;
      const uniqueModels = this.deduplicateModels(brandData.models);
      brandModelsMap[brand] = uniqueModels;
    });

    return brandModelsMap;
  }

  private deduplicateModels(models: BrandModelData['models']): Filter[] {
    const uniqueModels = new Map<string, Filter>();

    models.forEach((model) => {
      if (!uniqueModels.has(model.value)) {
        uniqueModels.set(model.value, {
          code: 'product_model',
          value: model.value,
          name: model.name,
        });
      }
    });

    return Array.from(uniqueModels.values());
  }
}
